import type { CaptureJob } from '@/lib/api/types';

/**
 * The job the operator is capturing for, and the last list of jobs this
 * device loaded (CAPTURE_JOBS.md section 6).
 *
 * The current job lives in `sessionStorage`: it is chosen per sitting, from
 * My jobs, so an app reopened tomorrow asks again rather than sending
 * captures into a job the manager may have closed overnight. The Capture
 * tab reads it to decide between the viewfinder and My jobs.
 *
 * The job list lives in `localStorage`, because an operator in a basement
 * aisle opens the app with no signal and still needs to see which jobs
 * they hold and what each one asks for.
 */

const CURRENT_JOB_KEY = 'smart-capture.current-job';
const JOBS_CACHE_KEY = 'smart-capture.jobs';

export interface CurrentJob {
  jobId: string;
  name: string;
}

function read<T>(storage: Storage | undefined, key: string): T | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: Storage | undefined, key: string, value: unknown): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing and a full quota both refuse writes. The app still
    // works; it only forgets the choice.
  }
}

function session(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

function local(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.localStorage;
}

export function rememberCurrentJob(job: CurrentJob): void {
  write(session(), CURRENT_JOB_KEY, { jobId: job.jobId, name: job.name });
}

export function readCurrentJob(): CurrentJob | null {
  const stored = read<CurrentJob>(session(), CURRENT_JOB_KEY);
  return stored && typeof stored.jobId === 'string' && stored.jobId !== '' ? stored : null;
}

export function forgetCurrentJob(): void {
  session()?.removeItem(CURRENT_JOB_KEY);
}

export interface CachedJobs {
  jobs: CaptureJob[];
  savedAt: string;
}

export function cacheJobs(jobs: CaptureJob[]): void {
  write(local(), JOBS_CACHE_KEY, { jobs, savedAt: new Date().toISOString() });
}

export function readCachedJobs(): CachedJobs | null {
  const stored = read<CachedJobs>(local(), JOBS_CACHE_KEY);
  return stored && Array.isArray(stored.jobs) ? stored : null;
}

/** The name of a job this device has seen, for a screen that holds only
 *  its id (the viewfinder, opened offline from a URL). */
export function knownJobName(jobId: string): string | null {
  const current = readCurrentJob();
  if (current?.jobId === jobId) return current.name;
  return readCachedJobs()?.jobs.find((job) => job.jobId === jobId)?.name ?? null;
}
